import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { env } from "./config/env";
import { useAudienceMode } from "./audience/AudienceModeProvider";
import { AccuracyCallout } from "./components/AccuracyCallout";
import { ActualBillCard } from "./components/ActualBillCard";
import { ExplorerVsModelTable } from "./components/ExplorerVsModelTable";
import { BudgetAlertBanner } from "./components/BudgetAlertBanner";
import { ComputeProfilePanel } from "./components/ComputeProfilePanel";
import { ChecklistCard } from "./components/ChecklistCard";
import { CostBreakdown } from "./components/CostBreakdown";
import { LlmProfilePanel } from "./components/LlmProfilePanel";
import { NamedScenariosPanel } from "./components/NamedScenariosPanel";
import { RegionCompareTable } from "./components/RegionCompareTable";
import { SiteFooter } from "./components/SiteFooter";
import { CostMixChart } from "./components/CostMixChart";
import { EstimatorToolbar } from "./components/EstimatorToolbar";
import { EstimatorForm } from "./components/EstimatorForm";
import { DeploymentStackPanel } from "./components/DeploymentStackPanel";
import { InfrastructurePlanCard } from "./components/InfrastructurePlanCard";
import { LiveEstimatePulse } from "./components/LiveEstimatePulse";
import { LoadAndBottlenecksCard } from "./components/LoadAndBottlenecksCard";
import { EnterpriseRoadmap } from "./components/EnterpriseRoadmap";
import { ExportReportPanel } from "./components/ExportReportPanel";
import { ScenarioSharePanel } from "./components/ScenarioSharePanel";
import { TrustPanel } from "./components/TrustPanel";
import { PlanningForm } from "./components/PlanningForm";
import { PresetChips } from "./components/PresetChips";
import { Recommendations } from "./components/Recommendations";
import { ScalingChart } from "./components/ScalingChart";
import { SavingsLeversPanel } from "./components/SavingsLeversPanel";
import { ScenarioCompare } from "./components/ScenarioCompare";
import { SensitivityCard } from "./components/SensitivityCard";
import { WhatIfSliders } from "./components/WhatIfSliders";
import { WorkloadJsonPreview } from "./components/WorkloadJsonPreview";
import { useI18n } from "./i18n/useI18n";
import { uiString } from "./i18n/ui";
import { normalizeAudienceMode } from "./lib/audienceMode";
import { normalizeAwsRegion, type AwsRegionId } from "./lib/awsRegion";
import {
  normalizeComputeProfile,
  type ComputeProfileId,
} from "./lib/computeProfile";
import { mergeChecklist, type ChecklistItemId } from "./lib/checklistIds";
import { normalizeInputMode, type InputMode } from "./lib/inputMode";
import { parseFeatureDescription } from "./lib/heuristicParser";
import {
  applyWorkloadOverrides,
  buildScalingCurve,
  estimateMonthlyCost,
} from "./lib/costEngine";
import { analyzeOptimization } from "./lib/optimizationEngine";
import { normalizeDisplayCurrency, type DisplayCurrency } from "./lib/displayCurrency";
import {
  loadPersistedUi,
  normalizeCostDisplayUnit,
  normalizeExplorerByService,
  savePersistedUi,
  type CostDisplayUnit,
  type PersistedUi,
} from "./lib/persistedUi";
import {
  addNamedScenario,
  loadNamedScenarios,
  removeNamedScenario,
  type NamedScenarioEntry,
} from "./lib/namedScenarios";
import { normalizeLlmPriceProfile, type LlmPriceProfile } from "./lib/llmPriceProfile";
import {
  decodeScenarioHashFragment,
  normalizeImportedPersisted,
} from "./lib/scenarioBundle";
import { coerceScenarioSnapshot, type ScenarioSnapshot } from "./lib/scenario";
import {
  normalizeDeploymentStack,
  type DeploymentStackConfig,
} from "./lib/deploymentStack";
import { normalizeSavingsLevers, type SavingsLevers } from "./lib/savingsLevers";
import { computeSensitivity } from "./lib/sensitivity";
import type { CostPerformanceMode, Workload } from "./lib/types";
import { coerceWorkload } from "./lib/workloadState";

function normalizeCpMode(x: unknown): CostPerformanceMode | undefined {
  if (x === "cheaper" || x === "balanced" || x === "faster") return x;
  return undefined;
}

function initialWorkload(
  persisted: ReturnType<typeof loadPersistedUi>,
  sampleText: string,
): Workload {
  const fromStore = persisted?.workload ? coerceWorkload(persisted.workload) : null;
  if (fromStore) return fromStore;
  const input = typeof persisted?.input === "string" ? persisted.input : sampleText;
  return parseFeatureDescription(input);
}

export function HomePage() {
  const { locale, t } = useI18n();
  const { audienceMode, setAudienceMode } = useAudienceMode();

  useEffect(() => {
    document.title = `${env.appName}${t("title.suffix")}`;
  }, [t, locale]);

  const persisted = useMemo(() => loadPersistedUi(), []);
  const sampleText = uiString(locale, "sampleInput");
  const initialInput =
    typeof persisted?.input === "string" ? persisted.input : sampleText;

  const [inputMode, setInputMode] = useState<InputMode>(() =>
    normalizeInputMode(persisted?.inputMode),
  );
  const [input, setInput] = useState(initialInput);
  const [parsed, setParsed] = useState<Workload>(() =>
    initialWorkload(persisted, sampleText),
  );
  const [userMult, setUserMult] = useState(() =>
    typeof persisted?.userMult === "number" ? persisted.userMult : 1,
  );
  const [reqMult, setReqMult] = useState(() =>
    typeof persisted?.reqMult === "number" ? persisted.reqMult : 1,
  );
  const [cpMode, setCpMode] = useState<CostPerformanceMode>(
    () => normalizeCpMode(persisted?.cpMode) ?? "balanced",
  );
  const [awsRegion, setAwsRegion] = useState<AwsRegionId>(() =>
    normalizeAwsRegion(persisted?.awsRegion),
  );
  const [budgetStr, setBudgetStr] = useState(() => {
    const n = persisted?.budgetCapUsd;
    if (typeof n === "number" && Number.isFinite(n) && n > 0)
      return String(Math.round(n));
    return "";
  });
  const [checklist, setChecklist] = useState(() =>
    mergeChecklist(persisted?.checklist),
  );
  const [scenarioA, setScenarioA] = useState<ScenarioSnapshot | null>(
    () => coerceScenarioSnapshot(persisted?.scenarioA) ?? null,
  );
  const [scenarioB, setScenarioB] = useState<ScenarioSnapshot | null>(
    () => coerceScenarioSnapshot(persisted?.scenarioB) ?? null,
  );
  const [savings, setSavings] = useState<SavingsLevers>(() =>
    normalizeSavingsLevers(persisted?.savings),
  );
  const [deploymentStack, setDeploymentStack] = useState<DeploymentStackConfig>(() =>
    normalizeDeploymentStack(persisted?.deploymentStack, persisted?.addonInfra),
  );
  const [llmPriceProfile, setLlmPriceProfile] = useState<LlmPriceProfile>(() =>
    normalizeLlmPriceProfile(persisted?.llmPriceProfile),
  );
  const [computeProfile, setComputeProfile] = useState<ComputeProfileId>(() =>
    normalizeComputeProfile(persisted?.computeProfile),
  );
  const [actualBillStr, setActualBillStr] = useState(() =>
    typeof persisted?.actualBillStr === "string" ? persisted.actualBillStr : "",
  );
  const [explorerByService, setExplorerByService] = useState<
    Record<string, number> | null
  >(() => normalizeExplorerByService(persisted?.explorerByService) ?? null);
  const [costDisplayUnit, setCostDisplayUnit] = useState<CostDisplayUnit>(() =>
    normalizeCostDisplayUnit(persisted?.costDisplayUnit),
  );
  const [namedScenarios, setNamedScenarios] = useState<NamedScenarioEntry[]>(() =>
    loadNamedScenarios(),
  );
  const [scenarioNotes, setScenarioNotes] = useState(() =>
    typeof persisted?.scenarioNotes === "string"
      ? persisted.scenarioNotes.slice(0, 4000)
      : "",
  );
  const [displayCurrency, setDisplayCurrency] = useState<DisplayCurrency>(() =>
    normalizeDisplayCurrency(persisted?.displayCurrency),
  );

  const fallbackInputRef = useRef(sampleText);
  fallbackInputRef.current = sampleText;

  const applyScenarioImport = useCallback(
    (p: PersistedUi) => {
      setInputMode(normalizeInputMode(p.inputMode));
      setInput(p.input);
      setParsed(p.workload ?? parseFeatureDescription(p.input));
      setUserMult(typeof p.userMult === "number" ? p.userMult : 1);
      setReqMult(typeof p.reqMult === "number" ? p.reqMult : 1);
      setCpMode(normalizeCpMode(p.cpMode) ?? "balanced");
      setAwsRegion(normalizeAwsRegion(p.awsRegion));
      const cap = p.budgetCapUsd;
      setBudgetStr(
        cap != null && typeof cap === "number" && Number.isFinite(cap) && cap > 0
          ? String(Math.round(cap))
          : "",
      );
      setChecklist(mergeChecklist(p.checklist));
      setScenarioA(coerceScenarioSnapshot(p.scenarioA) ?? null);
      setScenarioB(coerceScenarioSnapshot(p.scenarioB) ?? null);
      setSavings(normalizeSavingsLevers(p.savings));
      setDeploymentStack(normalizeDeploymentStack(p.deploymentStack, p.addonInfra));
      setLlmPriceProfile(normalizeLlmPriceProfile(p.llmPriceProfile));
      setComputeProfile(normalizeComputeProfile(p.computeProfile));
      setActualBillStr(typeof p.actualBillStr === "string" ? p.actualBillStr : "");
      setExplorerByService(normalizeExplorerByService(p.explorerByService) ?? null);
      setCostDisplayUnit(normalizeCostDisplayUnit(p.costDisplayUnit));
      setAudienceMode(normalizeAudienceMode(p.audienceMode));
      setScenarioNotes(
        typeof p.scenarioNotes === "string" ? p.scenarioNotes.slice(0, 4000) : "",
      );
      setDisplayCurrency(normalizeDisplayCurrency(p.displayCurrency));
    },
    [setAudienceMode],
  );

  useLayoutEffect(() => {
    const hash = window.location.hash;
    if (!hash.startsWith("#dm=1.")) return;
    const raw = decodeScenarioHashFragment(hash);
    const next = raw ? normalizeImportedPersisted(raw, fallbackInputRef.current) : null;
    if (!next) return;
    applyScenarioImport(next);
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}`,
    );
  }, [applyScenarioImport]);

  const budgetCapUsd = useMemo(() => {
    const raw = budgetStr.trim();
    if (!raw) return null;
    const n = Number(raw.replace(/,/g, ""));
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [budgetStr]);

  const scenarioSnapshot = useMemo<PersistedUi>(
    () => ({
      inputMode,
      input,
      workload: parsed,
      userMult,
      reqMult,
      cpMode,
      awsRegion,
      budgetCapUsd,
      checklist,
      scenarioA: scenarioA ?? undefined,
      scenarioB: scenarioB ?? undefined,
      savings,
      deploymentStack,
      llmPriceProfile,
      actualBillStr,
      costDisplayUnit,
      audienceMode,
      scenarioNotes: scenarioNotes.trim() ? scenarioNotes.slice(0, 4000) : undefined,
      displayCurrency,
      computeProfile,
      explorerByService: explorerByService ?? undefined,
    }),
    [
      inputMode,
      input,
      parsed,
      userMult,
      reqMult,
      cpMode,
      awsRegion,
      budgetCapUsd,
      checklist,
      scenarioA,
      scenarioB,
      savings,
      deploymentStack,
      llmPriceProfile,
      actualBillStr,
      costDisplayUnit,
      audienceMode,
      scenarioNotes,
      displayCurrency,
      computeProfile,
      explorerByService,
    ],
  );

  useEffect(() => {
    savePersistedUi({
      inputMode,
      input,
      workload: parsed,
      userMult,
      reqMult,
      cpMode,
      awsRegion,
      budgetCapUsd,
      checklist,
      scenarioA: scenarioA ?? undefined,
      scenarioB: scenarioB ?? undefined,
      savings,
      deploymentStack,
      llmPriceProfile,
      actualBillStr,
      costDisplayUnit,
      audienceMode,
      scenarioNotes: scenarioNotes.trim() ? scenarioNotes.slice(0, 4000) : undefined,
      displayCurrency,
      computeProfile,
      explorerByService: explorerByService ?? undefined,
    });
  }, [
    inputMode,
    input,
    parsed,
    userMult,
    reqMult,
    cpMode,
    awsRegion,
    budgetCapUsd,
    checklist,
    scenarioA,
    scenarioB,
    savings,
    deploymentStack,
    llmPriceProfile,
    actualBillStr,
    costDisplayUnit,
    audienceMode,
    scenarioNotes,
    displayCurrency,
    computeProfile,
    explorerByService,
  ]);

  const baseFromSliders = useMemo(() => {
    return applyWorkloadOverrides(parsed, {
      usersDaily: Math.round(parsed.usersDaily * userMult),
      requestsPerUser: Math.round(parsed.requestsPerUser * reqMult),
    });
  }, [parsed, userMult, reqMult]);

  const estimate = useMemo(
    () =>
      estimateMonthlyCost(
        baseFromSliders,
        locale,
        awsRegion,
        savings,
        deploymentStack,
        llmPriceProfile,
        audienceMode,
        computeProfile,
      ),
    [
      baseFromSliders,
      locale,
      awsRegion,
      savings,
      deploymentStack,
      llmPriceProfile,
      audienceMode,
      computeProfile,
    ],
  );

  const optimization = useMemo(
    () => analyzeOptimization(baseFromSliders, estimate, locale, audienceMode),
    [baseFromSliders, estimate, locale, audienceMode],
  );

  const scaling = useMemo(
    () =>
      buildScalingCurve(
        parsed,
        [1, 2, 5, 10],
        locale,
        awsRegion,
        savings,
        deploymentStack,
        llmPriceProfile,
        audienceMode,
        computeProfile,
      ),
    [
      parsed,
      locale,
      awsRegion,
      savings,
      deploymentStack,
      llmPriceProfile,
      audienceMode,
      computeProfile,
    ],
  );

  const sensitivity = useMemo(
    () =>
      computeSensitivity(
        baseFromSliders,
        locale,
        awsRegion,
        savings,
        deploymentStack,
        llmPriceProfile,
        audienceMode,
        computeProfile,
      ),
    [
      baseFromSliders,
      locale,
      awsRegion,
      savings,
      deploymentStack,
      llmPriceProfile,
      audienceMode,
      computeProfile,
    ],
  );

  const overBudget = budgetCapUsd !== null && estimate.totalMonthlyUsd > budgetCapUsd;

  function captureScenario(slot: "A" | "B") {
    const snap: ScenarioSnapshot = {
      workload: parsed,
      userMult,
      reqMult,
      totalMonthlyUsd: estimate.totalMonthlyUsd,
    };
    if (slot === "A") setScenarioA(snap);
    else setScenarioB(snap);
  }

  function applyChecklist(id: ChecklistItemId, checked: boolean) {
    setChecklist((prev) => ({ ...prev, [id]: checked }));
  }

  function handleParse() {
    setParsed(parseFeatureDescription(input));
    setUserMult(1);
    setReqMult(1);
  }

  const whatIfSub =
    inputMode === "planning" ? t("whatif.subPlanning") : t("whatif.subDescription");
  const whatIfAudienceSub =
    audienceMode === "simple"
      ? t("whatif.subAudienceSimple")
      : t("whatif.subAudienceTechnical");

  const simpleFlow = audienceMode === "simple";

  return (
    <div className="app">
      <header className="hero">
        <h1 className="hero-title-row">
          <img
            className="hero-logo"
            src={`${import.meta.env.BASE_URL}favicon.svg`}
            width={40}
            height={40}
            alt=""
            decoding="async"
          />
          <span>{t("hero.title")}</span>
        </h1>
        <p className="lede">
          {t("hero.lede.before")}
          <strong>{t("hero.lede.strong")}</strong>
          {t("hero.lede.after")}
        </p>
        <p className="hero-disclaimer">{t("hero.disclaimer")}</p>
      </header>

      <main className="grid">
        {simpleFlow ? (
          <details className="card span-2 flow-fold">
            <summary className="flow-fold__summary">
              {t("flow.detailsAccuracy")}
            </summary>
            <AccuracyCallout embedded t={t} audienceMode={audienceMode} />
          </details>
        ) : (
          <AccuracyCallout t={t} audienceMode={audienceMode} />
        )}
        <section className={"card span-2" + (simpleFlow ? " input-card--simple" : "")}>
          <div className="mode-tabs" role="tablist" aria-label={t("mode.aria")}>
            {(
              [
                ["planning", "mode.planning"],
                ["description", "mode.description"],
              ] as const
            ).map(([key, labelKey]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={inputMode === key}
                className={inputMode === key ? "mode-tab active" : "mode-tab"}
                onClick={() => setInputMode(key)}
              >
                {t(labelKey)}
              </button>
            ))}
          </div>

          {simpleFlow && inputMode === "description" ? (
            <p className="planning-cta">
              <button
                type="button"
                className="planning-cta__btn"
                onClick={() => setInputMode("planning")}
              >
                {t("form.tryPlanningTemplates")}
              </button>
              <span className="planning-cta__hint"> — {t("form.tryPlanningHint")}</span>
            </p>
          ) : null}

          {inputMode === "planning" ? (
            <>
              <PresetChips
                t={t}
                onApply={(w) => {
                  setParsed(w);
                  setUserMult(1);
                  setReqMult(1);
                }}
              />
              <PlanningForm workload={parsed} onChange={setParsed} />
            </>
          ) : (
            <EstimatorForm value={input} onChange={setInput} onSubmit={handleParse} />
          )}

          {!simpleFlow ? <WorkloadJsonPreview workload={parsed} /> : null}
        </section>

        {simpleFlow ? (
          <details className="card span-2 model-bundle flow-fold">
            <summary className="flow-fold__summary">{t("flow.detailsModel")}</summary>
            <h2 className="flow-h2 flow-fold__h2">{t("flow.section.model")}</h2>
            <div className="model-bundle-grid">
              <div className="model-bundle-cell">
                <SavingsLeversPanel t={t} value={savings} onChange={setSavings} />
              </div>
              <div className="model-bundle-cell">
                <LlmProfilePanel
                  t={t}
                  value={llmPriceProfile}
                  onChange={setLlmPriceProfile}
                />
              </div>
              <div className="model-bundle-cell">
                <ComputeProfilePanel
                  t={t}
                  value={computeProfile}
                  onChange={setComputeProfile}
                />
              </div>
              <div className="model-bundle-cell model-bundle-cell--wide">
                <DeploymentStackPanel
                  t={t}
                  value={deploymentStack}
                  onChange={setDeploymentStack}
                />
              </div>
            </div>
          </details>
        ) : (
          <section className="card span-2 model-bundle">
            <h2 className="flow-h2">{t("flow.section.model")}</h2>
            <div className="model-bundle-grid">
              <div className="model-bundle-cell">
                <SavingsLeversPanel t={t} value={savings} onChange={setSavings} />
              </div>
              <div className="model-bundle-cell">
                <LlmProfilePanel
                  t={t}
                  value={llmPriceProfile}
                  onChange={setLlmPriceProfile}
                />
              </div>
              <div className="model-bundle-cell">
                <ComputeProfilePanel
                  t={t}
                  value={computeProfile}
                  onChange={setComputeProfile}
                />
              </div>
              <div className="model-bundle-cell model-bundle-cell--wide">
                <DeploymentStackPanel
                  t={t}
                  value={deploymentStack}
                  onChange={setDeploymentStack}
                />
              </div>
            </div>
          </section>
        )}

        <section className="card span-2 utilities">
          {simpleFlow ? (
            <>
              <EstimatorToolbar
                locale={locale}
                t={t}
                region={awsRegion}
                onRegion={setAwsRegion}
                budgetStr={budgetStr}
                onBudgetStr={setBudgetStr}
                displayCurrency={displayCurrency}
                onDisplayCurrency={setDisplayCurrency}
              />
              <details className="flow-fold flow-fold--export">
                <summary className="flow-fold__summary">
                  {t("flow.detailsExport")}
                </summary>
                <div className="utilities-row utilities-row--stacked">
                  <ExportReportPanel
                    t={t}
                    region={awsRegion}
                    workload={baseFromSliders}
                    estimate={estimate}
                    optimization={optimization}
                    scaling={scaling}
                    sensitivity={sensitivity}
                    deploymentStack={deploymentStack}
                    audienceMode={audienceMode}
                    savings={savings}
                    llmPriceProfile={llmPriceProfile}
                    budgetCapUsd={budgetCapUsd}
                    scenarioNotes={scenarioNotes}
                    displayCurrency={displayCurrency}
                    namedScenarios={namedScenarios}
                    currentSnapshot={scenarioSnapshot}
                  />
                  <ScenarioSharePanel
                    t={t}
                    snapshot={scenarioSnapshot}
                    fallbackInput={sampleText}
                    onApply={applyScenarioImport}
                    scenarioNotes={scenarioNotes}
                    onScenarioNotesChange={setScenarioNotes}
                  />
                </div>
              </details>
            </>
          ) : (
            <div className="utilities-row">
              <EstimatorToolbar
                locale={locale}
                t={t}
                region={awsRegion}
                onRegion={setAwsRegion}
                budgetStr={budgetStr}
                onBudgetStr={setBudgetStr}
                displayCurrency={displayCurrency}
                onDisplayCurrency={setDisplayCurrency}
              />
              <ExportReportPanel
                t={t}
                region={awsRegion}
                workload={baseFromSliders}
                estimate={estimate}
                optimization={optimization}
                scaling={scaling}
                sensitivity={sensitivity}
                deploymentStack={deploymentStack}
                audienceMode={audienceMode}
                savings={savings}
                llmPriceProfile={llmPriceProfile}
                budgetCapUsd={budgetCapUsd}
                scenarioNotes={scenarioNotes}
                displayCurrency={displayCurrency}
                namedScenarios={namedScenarios}
                currentSnapshot={scenarioSnapshot}
              />
              <ScenarioSharePanel
                t={t}
                snapshot={scenarioSnapshot}
                fallbackInput={sampleText}
                onApply={applyScenarioImport}
                scenarioNotes={scenarioNotes}
                onScenarioNotesChange={setScenarioNotes}
              />
            </div>
          )}
        </section>

        {!simpleFlow ? (
          <section className="card span-2">
            <LoadAndBottlenecksCard
              t={t}
              workload={baseFromSliders}
              stack={deploymentStack}
              audienceMode={audienceMode}
            />
          </section>
        ) : null}

        <section className="card span-2">
          <WhatIfSliders
            subtitle={whatIfSub}
            audienceSub={whatIfAudienceSub}
            baseUsers={parsed.usersDaily}
            baseRequests={parsed.requestsPerUser}
            userMult={userMult}
            reqMult={reqMult}
            onUserMult={setUserMult}
            onReqMult={setReqMult}
            effective={baseFromSliders}
            totalMonthly={estimate.totalMonthlyUsd}
          />
        </section>

        <section className="card span-2">
          <LiveEstimatePulse
            locale={locale}
            t={t}
            estimate={estimate}
            displayCurrency={displayCurrency}
          />
        </section>

        {overBudget && (
          <section className="card span-2">
            <BudgetAlertBanner
              locale={locale}
              totalUsd={estimate.totalMonthlyUsd}
              capUsd={budgetCapUsd!}
            />
          </section>
        )}

        {simpleFlow ? (
          <section className="card span-2">
            <ActualBillCard
              t={t}
              value={actualBillStr}
              onChange={setActualBillStr}
              modelMonthlyUsd={estimate.totalMonthlyUsd}
              onExplorerParsed={(raw) =>
                setExplorerByService(normalizeExplorerByService(raw) ?? null)
              }
            />
            <ExplorerVsModelTable
              t={t}
              estimate={estimate}
              explorerByService={explorerByService}
              displayCurrency={displayCurrency}
              onClearExplorer={() => setExplorerByService(null)}
            />
          </section>
        ) : (
          <section className="card span-2 duo">
            <div className="duo-grid">
              <ActualBillCard
                t={t}
                value={actualBillStr}
                onChange={setActualBillStr}
                modelMonthlyUsd={estimate.totalMonthlyUsd}
                onExplorerParsed={(raw) =>
                  setExplorerByService(normalizeExplorerByService(raw) ?? null)
                }
              />
              <RegionCompareTable
                t={t}
                workload={baseFromSliders}
                savings={savings}
                stack={deploymentStack}
                llmProfile={llmPriceProfile}
                computeProfile={computeProfile}
                selectedRegion={awsRegion}
                audienceMode={audienceMode}
              />
            </div>
            <ExplorerVsModelTable
              t={t}
              estimate={estimate}
              explorerByService={explorerByService}
              displayCurrency={displayCurrency}
              onClearExplorer={() => setExplorerByService(null)}
            />
          </section>
        )}

        <section className="card span-2 results-bundle">
          <h2 className="flow-h2">{t("flow.section.results")}</h2>
          <div className="results-bundle-grid">
            <CostBreakdown
              estimate={estimate}
              effectiveWorkload={baseFromSliders}
              displayUnit={costDisplayUnit}
              onDisplayUnit={setCostDisplayUnit}
              audienceMode={audienceMode}
              displayCurrency={displayCurrency}
            />
            <CostMixChart locale={locale} t={t} estimate={estimate} />
          </div>
        </section>

        <section className="card span-2">
          <InfrastructurePlanCard
            workload={baseFromSliders}
            deploymentStack={deploymentStack}
            audienceMode={audienceMode}
            t={t}
          />
        </section>

        {!simpleFlow ? (
          <section className="card span-2 duo">
            <div className="duo-grid">
              <SensitivityCard locale={locale} t={t} deltas={sensitivity} />
              <ChecklistCard t={t} value={checklist} onChange={applyChecklist} />
            </div>
          </section>
        ) : (
          <section className="card span-2">
            <ChecklistCard t={t} value={checklist} onChange={applyChecklist} />
          </section>
        )}

        {!simpleFlow ? (
          <>
            <section className="card span-2">
              <ScenarioCompare
                locale={locale}
                t={t}
                scenarioA={scenarioA}
                scenarioB={scenarioB}
                currentTotalUsd={estimate.totalMonthlyUsd}
                onSaveA={() => captureScenario("A")}
                onSaveB={() => captureScenario("B")}
                onClearA={() => setScenarioA(null)}
                onClearB={() => setScenarioB(null)}
              />
            </section>

            <section className="card span-2">
              <NamedScenariosPanel
                t={t}
                entries={namedScenarios}
                currentSnapshot={scenarioSnapshot}
                onSave={(name, snap) =>
                  setNamedScenarios((prev) => addNamedScenario(name, snap, prev))
                }
                onLoad={applyScenarioImport}
                onDelete={(id) =>
                  setNamedScenarios((prev) => removeNamedScenario(id, prev))
                }
              />
            </section>

            <section className="card">
              <ScalingChart
                points={scaling}
                currentUsers={baseFromSliders.usersDaily}
              />
            </section>

            <section className="card">
              <div className="cp-block">
                <h2>{t("cp.title")}</h2>
                <div className="cp-tabs" role="tablist">
                  {(
                    [
                      ["cheaper", "cp.cheaper"],
                      ["balanced", "cp.balanced"],
                      ["faster", "cp.faster"],
                    ] as const
                  ).map(([key, labelKey]) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={cpMode === key}
                      className={cpMode === key ? "cp-tab active" : "cp-tab"}
                      onClick={() => setCpMode(key)}
                    >
                      {t(labelKey)}
                    </button>
                  ))}
                </div>
                <p className="cp-note">{optimization.costPerformanceNote[cpMode]}</p>
              </div>
            </section>
          </>
        ) : null}

        <section className="card span-2">
          <Recommendations optimization={optimization} />
        </section>

        {simpleFlow ? (
          <details className="card span-2 flow-fold">
            <summary className="flow-fold__summary">{t("flow.detailsExpert")}</summary>
            <div className="flow-fold__stack">
              <section className="card span-2 flow-fold__nest">
                <LoadAndBottlenecksCard
                  t={t}
                  workload={baseFromSliders}
                  stack={deploymentStack}
                  audienceMode={audienceMode}
                />
              </section>
              <section className="card span-2 flow-fold__nest">
                <RegionCompareTable
                  t={t}
                  workload={baseFromSliders}
                  savings={savings}
                  stack={deploymentStack}
                  llmProfile={llmPriceProfile}
                  computeProfile={computeProfile}
                  selectedRegion={awsRegion}
                  audienceMode={audienceMode}
                />
              </section>
              <section className="card span-2 flow-fold__nest">
                <SensitivityCard locale={locale} t={t} deltas={sensitivity} />
              </section>
              <section className="card span-2 flow-fold__nest">
                <ScenarioCompare
                  locale={locale}
                  t={t}
                  scenarioA={scenarioA}
                  scenarioB={scenarioB}
                  currentTotalUsd={estimate.totalMonthlyUsd}
                  onSaveA={() => captureScenario("A")}
                  onSaveB={() => captureScenario("B")}
                  onClearA={() => setScenarioA(null)}
                  onClearB={() => setScenarioB(null)}
                />
              </section>
              <section className="card span-2 flow-fold__nest">
                <NamedScenariosPanel
                  t={t}
                  entries={namedScenarios}
                  currentSnapshot={scenarioSnapshot}
                  onSave={(name, snap) =>
                    setNamedScenarios((prev) => addNamedScenario(name, snap, prev))
                  }
                  onLoad={applyScenarioImport}
                  onDelete={(id) =>
                    setNamedScenarios((prev) => removeNamedScenario(id, prev))
                  }
                />
              </section>
              <section className="card span-2 flow-fold__nest">
                <ScalingChart
                  points={scaling}
                  currentUsers={baseFromSliders.usersDaily}
                />
              </section>
              <section className="card span-2 flow-fold__nest">
                <div className="cp-block">
                  <h2>{t("cp.title")}</h2>
                  <div className="cp-tabs" role="tablist">
                    {(
                      [
                        ["cheaper", "cp.cheaper"],
                        ["balanced", "cp.balanced"],
                        ["faster", "cp.faster"],
                      ] as const
                    ).map(([key, labelKey]) => (
                      <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={cpMode === key}
                        className={cpMode === key ? "cp-tab active" : "cp-tab"}
                        onClick={() => setCpMode(key)}
                      >
                        {t(labelKey)}
                      </button>
                    ))}
                  </div>
                  <p className="cp-note">{optimization.costPerformanceNote[cpMode]}</p>
                </div>
              </section>
              <div className="duo-grid flow-fold__trust">
                <TrustPanel t={t} />
                <EnterpriseRoadmap t={t} />
              </div>
            </div>
          </details>
        ) : (
          <section className="card span-2 duo trust-enterprise-row">
            <div className="duo-grid">
              <TrustPanel t={t} />
              <EnterpriseRoadmap t={t} />
            </div>
          </section>
        )}
      </main>

      <SiteFooter t={t} />

      <style>{`
        .app {
          max-width: var(--main-content-max, 1120px);
          margin: 0 auto;
          padding: 2rem var(--main-content-pad-x, clamp(0.85rem, 4vw, 1.35rem)) 4rem;
        }
        .hero {
          margin-bottom: 2rem;
        }
        .hero-title-row {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          flex-wrap: wrap;
          font-size: clamp(1.75rem, 4vw, 2.25rem);
          font-weight: 700;
          margin: 0 0 0.75rem;
          letter-spacing: -0.02em;
          line-height: 1.15;
        }
        .hero-logo {
          border-radius: 8px;
          flex-shrink: 0;
          box-shadow: 0 0 0 1px var(--border);
        }
        .lede {
          color: var(--muted);
          max-width: 42rem;
          margin: 0;
        }
        .lede strong {
          color: var(--text);
        }
        .hero-disclaimer {
          font-size: 0.78rem;
          color: var(--muted);
          margin: 0.85rem 0 0;
          max-width: 44rem;
          line-height: 1.45;
        }
        .flow-h2 {
          font-size: 0.78rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.07em;
          color: var(--muted);
          margin: 0 0 1rem;
        }
        .model-bundle-grid {
          display: grid;
          gap: 1.15rem;
          grid-template-columns: 1fr;
        }
        .model-bundle-cell {
          padding: 0;
          min-width: 0;
        }
        @media (min-width: 800px) {
          .model-bundle-grid {
            grid-template-columns: 1fr 1fr;
          }
          .model-bundle-cell--wide {
            grid-column: 1 / -1;
          }
        }
        .results-bundle-grid {
          display: grid;
          gap: 1.25rem;
          grid-template-columns: 1fr;
          align-items: start;
        }
        @media (min-width: 900px) {
          .results-bundle-grid {
            grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
          }
        }
        .input-card--simple {
          background: linear-gradient(
            165deg,
            var(--surface) 0%,
            var(--surface2) 42%,
            var(--surface) 100%
          );
          box-shadow: 0 4px 28px -10px rgba(0, 0, 0, 0.35);
        }
        .planning-cta {
          margin: 0 0 1rem;
          padding: 0.65rem 0.8rem;
          border-radius: 10px;
          background: var(--accent-chip);
          border: 1px solid var(--accent-dim);
          font-size: 0.82rem;
          line-height: 1.5;
          color: var(--muted);
        }
        .planning-cta__btn {
          display: inline-flex;
          align-items: center;
          border: 1px solid var(--accent-dim);
          background: var(--surface2);
          color: var(--accent);
          font: inherit;
          font-weight: 600;
          font-size: 0.8rem;
          padding: 0.35rem 0.75rem;
          border-radius: 8px;
          cursor: pointer;
          margin-right: 0.35rem;
          vertical-align: middle;
        }
        .planning-cta__btn:hover {
          filter: brightness(1.06);
        }
        .planning-cta__btn:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 2px;
        }
        .planning-cta__hint {
          vertical-align: middle;
        }
        @media (max-width: 560px) {
          .planning-cta__btn {
            margin-bottom: 0.35rem;
          }
        }
        .mode-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1rem;
          padding-bottom: 0.85rem;
          border-bottom: 1px solid var(--border);
        }
        .mode-tab {
          border: 1px solid var(--border);
          background: var(--surface2);
          color: var(--muted);
          padding: 0.4rem 0.85rem;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 600;
          font-family: var(--font);
        }
        .mode-tab.active {
          border-color: var(--accent-dim);
          color: var(--accent);
          background: var(--accent-chip);
        }
        .grid {
          display: grid;
          gap: 1rem;
          grid-template-columns: 1fr 1fr;
        }
        @media (max-width: 800px) {
          .grid {
            grid-template-columns: 1fr;
            gap: 1.15rem;
          }
          .span-2 {
            grid-column: auto !important;
          }
        }
        @media (max-width: 640px) {
          .app {
            padding-top: 1.25rem;
            padding-bottom: 3rem;
          }
          .mode-tab {
            min-height: 44px;
            padding: 0.5rem 1rem;
          }
          .cp-tab {
            min-height: 44px;
            padding: 0.45rem 0.9rem;
          }
        }
        .span-2 {
          grid-column: 1 / -1;
        }
        .utilities-row {
          display: grid;
          grid-template-columns: minmax(0, 1.35fr) minmax(0, 1.05fr) minmax(0, 1.05fr);
          gap: 0 1.25rem;
          align-items: start;
        }
        .utilities-row > * {
          min-width: 0;
        }
        @media (max-width: 900px) {
          .utilities-row {
            grid-template-columns: 1fr;
            gap: 0;
          }
        }
        .duo-grid {
          display: grid;
          gap: 1.5rem;
          grid-template-columns: 1fr 1fr;
        }
        @media (max-width: 700px) {
          .duo-grid {
            grid-template-columns: 1fr;
          }
        }
        .card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 1.25rem 1.35rem;
        }
        .cp-block h2 {
          font-size: 1rem;
          margin: 0 0 0.75rem;
        }
        .cp-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
        }
        .cp-tab {
          border: 1px solid var(--border);
          background: var(--surface2);
          color: var(--muted);
          padding: 0.35rem 0.75rem;
          border-radius: 8px;
          cursor: pointer;
        }
        .cp-tab.active {
          border-color: var(--accent-dim);
          color: var(--text);
          background: var(--accent-chip);
        }
        .cp-note {
          margin: 0;
          color: var(--muted);
          font-size: 0.95rem;
        }
        .flow-fold__summary {
          cursor: pointer;
          font-weight: 600;
          font-size: 0.88rem;
          color: var(--text);
          list-style: none;
          padding: 0.1rem 0;
        }
        .flow-fold__summary::-webkit-details-marker {
          display: none;
        }
        .flow-fold[open] > .flow-fold__summary {
          margin-bottom: 0.65rem;
          padding-bottom: 0.55rem;
          border-bottom: 1px solid var(--border);
        }
        .flow-fold--export {
          margin-top: 1rem;
          padding: 0.5rem 0.65rem;
          border-radius: 10px;
          border: 1px solid var(--border);
          background: var(--surface2);
        }
        .flow-fold--export .flow-fold__summary {
          font-size: 0.82rem;
        }
        .utilities-row--stacked {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          align-items: stretch;
        }
        .utilities-row--stacked > * {
          border-left: none !important;
          padding-left: 0 !important;
        }
        .flow-fold__h2 {
          margin-top: 0;
        }
        .flow-fold__stack {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .flow-fold__nest {
          margin: 0;
        }
        .flow-fold__trust {
          display: grid;
          gap: 1rem;
          grid-template-columns: 1fr 1fr;
        }
        @media (max-width: 700px) {
          .flow-fold__trust {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
